import React, { FC } from 'react';
import { Divider, Grid } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { PaperCard } from 'components/cards/paper-card';
import { TFunction } from 'i18next';
import { AccountingType } from 'v2-types/order';
import { getAccountingEntryByLedgerCode } from 'utils/accounting';
import {
  LEDGER_CODE_ALL_DISBURSEMENTS,
  LEDGER_CODE_BALANCE,
  LEDGER_CODE_BORROWER_OWES,
  LEDGER_CODE_EARNEST_OWED,
  LEDGER_CODE_EXPECTED_RECEIPTS,
  LEDGER_CODE_LENDER_OWES,
  LEDGER_CODE_OTHERS_DUE,
  LEDGER_CODE_POSTED_DISBURSEMENTS,
  LEDGER_CODE_POSTED_RECEIPTS,
  LEDGER_CODE_SELLER_DUE
} from 'utils/constants';

import { BalancingItem } from './balancing-item';

const useStyles = makeStyles((theme: any) => ({
  divider: { backgroundColor: theme.palette.tab.offselected, opacity: 0.5 }
}));

type SettlementBalancingOverviewProps = {
  accounting: AccountingType[];
  t: TFunction;
};

export const SettlementBalancingOverview: FC<SettlementBalancingOverviewProps> = ({
  accounting,
  t,
}) => {
  const classes = useStyles();

  const getEntryByLedgerCode = (ledgerCode: string) =>
    getAccountingEntryByLedgerCode({ accounting, ledgerCode });

  const getEffectiveAmount = (entry: any): number => {
    if (!entry) return 0;
    return entry.amount_override !== 0 ? entry.amount_override : entry.amount_calculated;
  };

  const expectedReceipts = getEntryByLedgerCode(LEDGER_CODE_EXPECTED_RECEIPTS)?.amount || 0;
  const postedDisbursements = getEntryByLedgerCode(LEDGER_CODE_POSTED_DISBURSEMENTS)?.amount || 0;
  const lenderOwes = getEntryByLedgerCode(LEDGER_CODE_LENDER_OWES)?.amount || 0;
  const borrowerOwes = getEntryByLedgerCode(LEDGER_CODE_BORROWER_OWES)?.amount || 0;
  const earnestOwed = getEntryByLedgerCode(LEDGER_CODE_EARNEST_OWED)?.amount || 0;
  const sellerDue = getEntryByLedgerCode(LEDGER_CODE_SELLER_DUE)?.amount || 0;
  const othersDue = getEntryByLedgerCode(LEDGER_CODE_OTHERS_DUE)?.amount || 0;
  const balanced = getEntryByLedgerCode(LEDGER_CODE_BALANCE)?.amount || 0;

  // Formula: balance = expectedReceipts - postedDisbursements + lenderOwes + borrowerOwes + earnestOwed + sellerDue + othersDue
  const computedBalance = expectedReceipts - postedDisbursements + lenderOwes + borrowerOwes + earnestOwed + sellerDue + othersDue;

  return (
    <PaperCard title={t('accounting:balancing')}>
      <Grid container direction="column" spacing={1}>
        <Grid item>
          <BalancingItem
            title={t('accounting:expected-receipts')}
            value={expectedReceipts}
            isPositiveIncome
          />
        </Grid>
        <Grid item>
          <BalancingItem
            title={t('accounting:posted-disbursements')}
            value={postedDisbursements}
            isNegativeIncome
          />
        </Grid>
        <Grid item>
          <BalancingItem
            title={t('accounting:lender-owes')}
            value={lenderOwes}
            isPositiveIncome
          />
        </Grid>
        <Grid item>
          <BalancingItem
            title={t('accounting:borrower-owes')}
            value={borrowerOwes}
            isPositiveIncome
          />
        </Grid>
        <Grid item>
          <BalancingItem
            title={t('accounting:earnest-owed')}
            value={earnestOwed}
            isPositiveIncome
          />
        </Grid>
        <Grid item>
          <BalancingItem
            title={t('accounting:seller-due')}
            value={sellerDue}
            isPositiveIncome
          />
        </Grid>
        <Grid item>
          <BalancingItem
            title={t('accounting:others-due')}
            value={othersDue}
            isPositiveIncome
          />
        </Grid>
        <Grid item>
          <Divider className={classes.divider} />
        </Grid>
        <Grid item>
          <BalancingItem
            title={t('accounting:balanced')}
            value={computedBalance}
            isPositiveIncome
          />
        </Grid>
      </Grid>
    </PaperCard>
  );
};
